import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PeopleField } from "../components/PeoplePicker";
import { endpoints, type Me, type UnfinishedUpload } from "../lib/api";
import {
  AUDIO_EXTENSIONS,
  MAX_UPLOAD_BYTES,
  VIDEO_EXTENSIONS,
  bytes,
  extensionOf,
  fileDate,
  sendFile,
  type UploadProgress,
} from "../lib/fileUpload";

type Choice = "auto" | "meet" | "teams" | "zoom" | "in_person";
type Phase = "pick" | "uploading" | "done";

const ACCEPT = [...VIDEO_EXTENSIONS, ...AUDIO_EXTENSIONS, "audio/*", "video/*"].join(",");
const CHOICES: { value: Choice; label: string }[] = [
  { value: "auto", label: "Work it out" },
  { value: "meet", label: "Google Meet" },
  { value: "teams", label: "Microsoft Teams" },
  { value: "zoom", label: "Zoom" },
  { value: "in_person", label: "In person" },
];

/** Upload a recording you already have — a meeting recorded in Meet, Teams or
 * Zoom, or a voice memo — and it becomes a meeting like any other: transcript,
 * speaker names, minutes. Sent in pieces, so a dropped connection carries on,
 * and a closed tab can pick up where it stopped. */
export function UploadPage({ me }: { me: Me | null }) {
  const queryClient = useQueryClient();
  const csrfToken = me?.csrf_token ?? "";
  const unfinished = useQuery({ queryKey: ["uploads-unfinished"], queryFn: endpoints.unfinishedUploads });
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState("");
  const [attendees, setAttendees] = useState<string[]>([]);
  const [platform, setPlatform] = useState<Choice>("auto");
  const [phase, setPhase] = useState<Phase>("pick");
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const stopper = useRef<AbortController | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const taken = useMemo(() => new Set(me ? [me.email.toLowerCase()] : []), [me]);
  const domain = me?.email.split("@")[1] ?? "";

  // The same file, started before and never finished: carry on from there.
  const earlier = file ? (unfinished.data ?? []).find((u) => u.file_name === file.name && u.size === file.size) : undefined;

  // Leaving mid-upload loses the piece on its way, nothing more — but say so.
  useEffect(() => {
    if (phase !== "uploading") return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [phase]);

  function choose(picked: File | undefined) {
    setError(null);
    if (!picked) return;
    const extension = extensionOf(picked.name);
    if (![...AUDIO_EXTENSIONS, ...VIDEO_EXTENSIONS].includes(extension)) {
      setError("That isn't an audio or video file this can read. Try MP4, MOV, MKV, WebM, MP3, M4A or WAV.");
      return;
    }
    if (picked.size > MAX_UPLOAD_BYTES) {
      setError(`That file is ${bytes(picked.size)}; the limit is 2 GB.`);
      return;
    }
    setFile(picked);
    setTitle(picked.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim());
    setWhen(localInput(fileDate(picked)));
  }

  async function upload(resume?: UnfinishedUpload) {
    if (!file) return;
    setError(null);
    setPhase("uploading");
    const controller = new AbortController();
    stopper.current = controller;
    try {
      let id = resume?.session_id;
      let pieceBytes = resume?.piece_bytes ?? 0;
      let next = resume?.next ?? 0;
      if (!id) {
        const started = await endpoints.startUpload({
          file_name: file.name, size: file.size, title, attendees, platform,
          starts_at: when ? new Date(when).toISOString() : null,
        }, csrfToken);
        id = started.session_id;
        pieceBytes = started.piece_bytes;
        next = started.next;
      }
      setSessionId(id);
      setProgress({ sent: next * pieceBytes, size: file.size, problem: null });
      await sendFile(file, id, pieceBytes, next, csrfToken, setProgress, controller.signal);
      await endpoints.finishUpload(id, csrfToken);
      setPhase("done");
      queryClient.invalidateQueries({ queryKey: ["uploads-unfinished"] });
      queryClient.invalidateQueries({ queryKey: ["recordings"] });
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
    } catch (caught) {
      const stopped = caught instanceof DOMException && caught.name === "AbortError";
      setError(stopped ? "Upload paused. Pick the same file again to carry on from where it stopped."
        : (caught as Error).message);
      setPhase("pick");
      queryClient.invalidateQueries({ queryKey: ["uploads-unfinished"] });
    } finally {
      stopper.current = null;
    }
  }

  function again() {
    setFile(null);
    setTitle("");
    setWhen("");
    setAttendees([]);
    setPlatform("auto");
    setProgress(null);
    setSessionId(null);
    setError(null);
    setPhase("pick");
  }

  const discard = useMutation({
    mutationFn: (id: string) => endpoints.deleteUpload(id, csrfToken),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["uploads-unfinished"] }),
  });

  const percent = progress && progress.size ? Math.floor((progress.sent / progress.size) * 100) : 0;
  const isAudio = file ? AUDIO_EXTENSIONS.includes(extensionOf(file.name)) : false;
  return (
    <div className="page-body upload-page">
      <header className="record-head">
        <h1>Upload a recording</h1>
        <p>A meeting recorded in Meet, Teams or Zoom, or a voice recording from a phone. It becomes a meeting like
          any other: transcript, speaker names and minutes, a few minutes after it's uploaded. Up to 2 GB and 3 hours.</p>
      </header>

      {phase === "pick" && (
        <>
          <section
            className={`upload-drop${dragging ? " over" : ""}${file ? " chosen" : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              choose(event.dataTransfer.files[0]);
            }}
          >
            <input ref={picker} type="file" accept={ACCEPT} hidden
                   onChange={(event) => {
                     choose(event.target.files?.[0]);
                     event.target.value = "";
                   }} />
            <span className="upload-icon" aria-hidden><IconUpload /></span>
            {file ? (
              <>
                <strong className="upload-name">{file.name}</strong>
                <span className="upload-sub">{bytes(file.size)} · {isAudio ? "audio" : "video"}</span>
                <button className="btn-quiet" onClick={() => picker.current?.click()}>Choose another file</button>
              </>
            ) : (
              <>
                <strong>Drop a recording here</strong>
                <span className="upload-sub">MP4, MOV, MKV, WebM, MP3, M4A, WAV and more</span>
                <button className="btn-primary" onClick={() => picker.current?.click()}>Choose a file</button>
              </>
            )}
          </section>

          {file && earlier && (
            <section className="upload-resume">
              <span>
                <strong>You started uploading this file before.</strong> {bytes(earlier.bytes)} of {bytes(earlier.size)} arrived.
              </span>
              <span className="upload-resume-actions">
                <button className="btn-quiet" onClick={() => upload()}>Start over</button>
                <button className="btn-primary" onClick={() => upload(earlier)}>Carry on</button>
              </span>
            </section>
          )}

          {file && (
            <section className="upload-form">
              <label className="record-field">
                <span>Title</span>
                <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120}
                       placeholder="Weekly sync" />
              </label>
              <label className="record-field">
                <span>When it happened</span>
                <input type="datetime-local" value={when} onChange={(event) => setWhen(event.target.value)} />
              </label>
              <div className="record-field">
                <span>Who was there <small>(optional — they'll see it, and it helps name the voices)</small></span>
                <PeopleField chosen={attendees} onChange={setAttendees} taken={taken} domain={domain}
                             label="Add people by name or email" />
              </div>
              <label className="record-field">
                <span>Recorded in</span>
                <select value={platform} onChange={(event) => setPlatform(event.target.value as Choice)}>
                  {CHOICES.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
                </select>
                <small className="upload-hint">
                  {platform !== "auto" ? "" : isAudio
                    ? "Sound only counts as in person; change it if it was a call."
                    : "The video is looked at to tell Meet, Teams and Zoom apart."}
                </small>
              </label>
              <div className="upload-go">
                <button className="btn-primary" disabled={!csrfToken} onClick={() => upload()}>Upload</button>
              </div>
            </section>
          )}
          {error && <div className="error">{error}</div>}
        </>
      )}

      {phase === "uploading" && file && (
        <section className="card upload-progress">
          <div className="upload-progress-head">
            <strong>{title || file.name}</strong>
            <span>{percent}%</span>
          </div>
          <div className="upload-bar" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: `${percent}%` }} />
          </div>
          <p className="upload-sub">
            {progress ? `${bytes(progress.sent)} of ${bytes(progress.size)}` : "Starting…"} · keep this page open until it's done
          </p>
          {progress?.problem && <p className="upload-problem">{progress.problem}</p>}
          <button className="btn-quiet" onClick={() => stopper.current?.abort()}>Pause</button>
        </section>
      )}

      {phase === "done" && sessionId && (
        <section className="card upload-progress">
          <strong>Uploaded</strong>
          <p className="upload-sub">It's being prepared and transcribed now. The transcript, speaker names and minutes
            appear on its page in a few minutes; the page updates by itself.</p>
          <div className="upload-done-actions">
            <Link className="btn-primary" to={`/meetings/${sessionId}`}>Open the meeting</Link>
            <button className="btn-quiet" onClick={again}>Upload another</button>
          </div>
        </section>
      )}

      {phase === "pick" && (unfinished.data?.length ?? 0) > 0 && (
        <section className="card record-unfinished">
          <h2 className="card-title">Unfinished uploads</h2>
          <p className="doc-note">These stopped part of the way. Pick the same file above to carry on from where it stopped.</p>
          <ul>
            {unfinished.data!.map((old) => (
              <li key={old.session_id}>
                <span>
                  <strong>{old.title}</strong>
                  <small>
                    {old.file_name} · {bytes(old.bytes)} of {bytes(old.size)} · started {new Date(old.created_at).toLocaleString()}
                  </small>
                </span>
                <span className="record-unfinished-actions">
                  <button
                    className="btn-quiet danger"
                    disabled={discard.isPending}
                    onClick={() => {
                      if (window.confirm(`Delete the unfinished upload “${old.title}”?`)) discard.mutate(old.session_id);
                    }}
                  >
                    Delete
                  </button>
                </span>
              </li>
            ))}
          </ul>
          {discard.error && <div className="error">{(discard.error as Error).message}</div>}
        </section>
      )}
    </div>
  );
}

/** A date as a datetime-local input wants it: local time, to the minute. */
function localInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function IconUpload() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" />
    </svg>
  );
}
